import { useState, useEffect, useCallback } from 'react'
import { isPreviewMode } from '@/lib/utils'

interface PreviewMessage {
  type: 'PREVIEW_CONTENT'
  sectionKey: string
  content: Record<string, unknown>
}

interface UsePreviewReturn {
  isPreview: boolean
  previewContent: Record<string, unknown> | null
}

export function usePreview(sectionKey: string): UsePreviewReturn {
  const [previewContent, setPreviewContent] = useState<Record<string, unknown> | null>(null)
  const isPreview = isPreviewMode()

  useEffect(() => {
    if (!isPreview) return

    const handleMessage = (event: MessageEvent<PreviewMessage>) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) return

      if (
        event.data?.type === 'PREVIEW_CONTENT' &&
        event.data?.sectionKey === sectionKey
      ) {
        setPreviewContent(event.data.content)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [sectionKey, isPreview])

  return {
    isPreview,
    previewContent,
  }
}

export function useSendPreview() {
  const sendPreview = useCallback(
    (
      iframe: HTMLIFrameElement | null,
      sectionKey: string,
      content: Record<string, unknown>
    ) => {
      if (!iframe?.contentWindow) return

      const message: PreviewMessage = {
        type: 'PREVIEW_CONTENT',
        sectionKey,
        content,
      }

      iframe.contentWindow.postMessage(message, window.location.origin)
    },
    []
  )

  return { sendPreview }
}
